/**
 * Shared drink metrics. One implementation used by UI, tests, and build scripts.
 *
 * Internal volume: milliliters.
 * ABV: percentage points (4.2 means 4.2%).
 * 1 US fl oz = 29.5735295625 mL (exact).
 * STANDARD_ALCOHOL_ML ≈ 14 g ethanol by volume convention; do not mix with gram-based paths.
 */

export const ML_PER_US_FL_OZ = 29.5735295625;
export const STANDARD_ALCOHOL_ML = 0.6 * ML_PER_US_FL_OZ; // 17.7441177375
export const ALCOHOL_DENSITY_G_PER_ML = 0.789;
export const KCAL_PER_G_ALCOHOL = 7;
/** Approximate alcohol energy (kcal) for a given pure-alcohol volume. */
export const ALCOHOL_KCAL_PER_ML =
  ALCOHOL_DENSITY_G_PER_ML * KCAL_PER_G_ALCOHOL;

export const EQUALITY_TOLERANCE = 1e-9;

export function mlToFlOz(ml: number): number {
  return ml / ML_PER_US_FL_OZ;
}

export function flOzToMl(flOz: number): number {
  return flOz * ML_PER_US_FL_OZ;
}

export function litersToMl(liters: number): number {
  return liters * 1000;
}

export function mlToLiters(ml: number): number {
  return ml / 1000;
}

export function convertToMl(
  value: number,
  unit: 'ml' | 'fl_oz' | 'L',
): number {
  if (unit === 'ml') return value;
  if (unit === 'fl_oz') return flOzToMl(value);
  return litersToMl(value);
}

export function convertFromMl(
  ml: number,
  unit: 'ml' | 'fl_oz' | 'L',
): number {
  if (unit === 'ml') return ml;
  if (unit === 'fl_oz') return mlToFlOz(ml);
  return mlToLiters(ml);
}

export function pureAlcoholMl(
  servingMl: number,
  abvPercent: number,
): number {
  return servingMl * (abvPercent / 100);
}

/** Pure alcohol from N catalog servings of a drink. */
export function alcoholMlForServings(
  servings: number,
  servingMl: number,
  abvPercent: number,
): number | null {
  if (!(servings >= 0) || !(servingMl > 0) || !(abvPercent > 0)) return null;
  return servings * pureAlcoholMl(servingMl, abvPercent);
}

/**
 * How many catalog servings of a drink deliver `targetAlcoholMl` of pure alcohol.
 * Example: 2× of 4% / 12 oz beer → servings of 5% / 12 oz to match alcohol.
 */
export function servingsToMatchAlcohol(
  targetAlcoholMl: number,
  servingMl: number,
  abvPercent: number,
): number | null {
  const perServing = pureAlcoholMl(servingMl, abvPercent);
  if (!(perServing > 0) || !(targetAlcoholMl >= 0) || !Number.isFinite(targetAlcoholMl)) {
    return null;
  }
  return targetAlcoholMl / perServing;
}

export function totalCaloriesForServings(
  servings: number,
  caloriesPerServing: number | null | undefined,
): number | null {
  if (caloriesPerServing == null || !(servings >= 0) || !(caloriesPerServing >= 0)) {
    return null;
  }
  return servings * caloriesPerServing;
}

export function standardDrinks(
  servingMl: number,
  abvPercent: number,
): number {
  return pureAlcoholMl(servingMl, abvPercent) / STANDARD_ALCOHOL_ML;
}

export function alcoholPer100Calories(
  servingMl: number,
  abvPercent: number,
  calories: number,
): number | null {
  if (!(calories > 0) || !(abvPercent > 0) || !(servingMl > 0)) return null;
  return (pureAlcoholMl(servingMl, abvPercent) / calories) * 100;
}

export function standardDrinksPer100Calories(
  servingMl: number,
  abvPercent: number,
  calories: number,
): number | null {
  if (!(calories > 0) || !(abvPercent > 0) || !(servingMl > 0)) return null;
  return (standardDrinks(servingMl, abvPercent) / calories) * 100;
}

export function caloriesPerStandardDrink(
  servingMl: number,
  abvPercent: number,
  calories: number,
): number | null {
  const sd = standardDrinks(servingMl, abvPercent);
  if (!(sd > 0) || !(calories >= 0)) return null;
  return calories / sd;
}

export function packageMl(
  containerCount: number,
  containerMl: number,
): number {
  return containerCount * containerMl;
}

export function packageAlcoholMl(
  containerCount: number,
  containerMl: number,
  abvPercent: number,
): number {
  return packageMl(containerCount, containerMl) * (abvPercent / 100);
}

export function packageStandardDrinks(
  containerCount: number,
  containerMl: number,
  abvPercent: number,
): number {
  return (
    packageAlcoholMl(containerCount, containerMl, abvPercent) /
    STANDARD_ALCOHOL_ML
  );
}

export function alcoholPerDollar(
  containerCount: number,
  containerMl: number,
  abvPercent: number,
  packagePriceUsd: number,
): number | null {
  if (!(packagePriceUsd > 0) || !(abvPercent > 0)) return null;
  return (
    packageAlcoholMl(containerCount, containerMl, abvPercent) /
    packagePriceUsd
  );
}

/**
 * @deprecated Scale-dependent (halving serving size doubles the score). Kept only
 * for old tests; not used in product rankings. Prefer the two primary ratios or
 * “better on both” (calories/std + $/std).
 */
export function alcoholPer100CaloriesPerDollar(
  alcoholPer100Cal: number | null,
  servingPriceUsd: number | null,
): number | null {
  if (alcoholPer100Cal == null || !(servingPriceUsd != null && servingPriceUsd > 0)) {
    return null;
  }
  return alcoholPer100Cal / servingPriceUsd;
}

export function standardDrinksPerDollar(
  containerCount: number,
  containerMl: number,
  abvPercent: number,
  packagePriceUsd: number,
): number | null {
  if (!(packagePriceUsd > 0) || !(abvPercent > 0)) return null;
  return (
    packageStandardDrinks(containerCount, containerMl, abvPercent) /
    packagePriceUsd
  );
}

export function dollarsPerStandardDrink(
  containerCount: number,
  containerMl: number,
  abvPercent: number,
  packagePriceUsd: number,
): number | null {
  const sd = packageStandardDrinks(containerCount, containerMl, abvPercent);
  if (!(sd > 0) || !(packagePriceUsd > 0)) return null;
  return packagePriceUsd / sd;
}

export function servingPriceUsd(
  packagePriceUsd: number,
  servingMl: number,
  containerCount: number,
  containerMl: number,
): number | null {
  const pkg = packageMl(containerCount, containerMl);
  if (!(pkg > 0) || !(packagePriceUsd > 0) || !(servingMl > 0)) return null;
  return packagePriceUsd * (servingMl / pkg);
}

/** Baseline comparisons: A = candidate, B = baseline. */
export function alcoholPerCalorieGainPct(
  aAlcoholPer100Cal: number,
  bAlcoholPer100Cal: number,
): number | null {
  if (!(bAlcoholPer100Cal > 0) || !(aAlcoholPer100Cal >= 0)) return null;
  return (aAlcoholPer100Cal / bAlcoholPer100Cal - 1) * 100;
}

export function calorieSavingsPct(
  aCaloriesPerStd: number,
  bCaloriesPerStd: number,
): number | null {
  if (!(bCaloriesPerStd > 0) || !(aCaloriesPerStd >= 0)) return null;
  return (1 - aCaloriesPerStd / bCaloriesPerStd) * 100;
}

export function priceSavingsPct(
  aDollarsPerStd: number,
  bDollarsPerStd: number,
): number | null {
  if (!(bDollarsPerStd > 0) || !(aDollarsPerStd >= 0)) return null;
  return (1 - aDollarsPerStd / bDollarsPerStd) * 100;
}

/**
 * Preference ranking (phase-two optional). Lower is better.
 * w=1 calories only; w=0 price only.
 */
export function weightedGeometricCost(
  caloriesPerStd: number,
  dollarsPerStd: number,
  baselineCaloriesPerStd: number,
  baselineDollarsPerStd: number,
  w: number,
): number | null {
  if (
    !(caloriesPerStd > 0) ||
    !(dollarsPerStd > 0) ||
    !(baselineCaloriesPerStd > 0) ||
    !(baselineDollarsPerStd > 0) ||
    w < 0 ||
    w > 1
  ) {
    return null;
  }
  const calRatio = caloriesPerStd / baselineCaloriesPerStd;
  const priceRatio = dollarsPerStd / baselineDollarsPerStd;
  return Math.pow(calRatio, w) * Math.pow(priceRatio, 1 - w);
}

export function estimatedAlcoholEnergyKcal(pureAlcoholMlValue: number): number {
  return pureAlcoholMlValue * ALCOHOL_KCAL_PER_ML;
}

export function isFiniteNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function nearlyEqual(
  a: number,
  b: number,
  tolerance = EQUALITY_TOLERANCE,
): boolean {
  return Math.abs(a - b) <= tolerance;
}

export function formatMetric(
  value: number | null,
  digits = 3,
): string {
  if (!isFiniteNumber(value)) return '—';
  return value.toFixed(digits);
}

/** Always show money to the cent (half-up via currency formatter). */
export function formatMoney(usd: number | null | undefined): string {
  if (usd == null || !Number.isFinite(usd)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(usd);
}

/**
 * Snap retail pack sizes to exact US conventions so 355 mL cans match
 * 12.00 fl oz servings (avoids 13.891 vs 13.887 alcohol/$ drift).
 */
export function canonicalizeRetailMl(ml: number): number {
  if (!(ml > 0)) return ml;
  const flOzTargets = [
    { flOz: 7, tol: 1 },
    { flOz: 8, tol: 1 },
    { flOz: 12, tol: 1.25 },
    { flOz: 16, tol: 1.5 },
    { flOz: 19.2, tol: 2 },
    { flOz: 22, tol: 2 },
    { flOz: 24, tol: 2 },
    { flOz: 32, tol: 2 },
    { flOz: 40, tol: 2.5 },
  ];
  for (const t of flOzTargets) {
    const exact = flOzToMl(t.flOz);
    if (Math.abs(ml - exact) <= t.tol) return exact;
  }
  const metricTargets = [
    { ml: 50, tol: 2 },
    { ml: 100, tol: 3 },
    { ml: 200, tol: 4 },
    { ml: 375, tol: 5 },
    { ml: 700, tol: 8 },
    { ml: 750, tol: 8 },
    { ml: 1000, tol: 10 },
    { ml: 1750, tol: 15 },
  ];
  for (const t of metricTargets) {
    if (Math.abs(ml - t.ml) <= t.tol) return t.ml;
  }
  return ml;
}

export function formatPercent(value: number | null, digits = 1): string {
  if (!isFiniteNumber(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(digits)}%`;
}

export function savingsWording(
  savingsPct: number | null,
  kind: 'calories' | 'price',
): string {
  if (!isFiniteNumber(savingsPct)) return 'Insufficient data';
  if (nearlyEqual(savingsPct, 0, 0.05)) {
    return kind === 'calories'
      ? 'About the same calories for the same alcohol'
      : 'About the same price for the same alcohol';
  }
  if (savingsPct > 0) {
    return kind === 'calories'
      ? `${savingsPct.toFixed(1)}% fewer calories for the same alcohol`
      : `${savingsPct.toFixed(1)}% cheaper for the same alcohol`;
  }
  const more = Math.abs(savingsPct).toFixed(1);
  return kind === 'calories'
    ? `${more}% more calories for the same alcohol`
    : `${more}% more expensive for the same alcohol`;
}

export interface ServingMetrics {
  pureAlcoholMl: number | null;
  pureAlcoholFlOz: number | null;
  standardDrinks: number | null;
  alcoholPer100Calories: number | null;
  alcoholPer100CaloriesFlOz: number | null;
  standardDrinksPer100Calories: number | null;
  caloriesPerStandardDrink: number | null;
}

export function computeServingMetrics(
  servingMl: number,
  abvPercent: number | null,
  calories: number | null,
): ServingMetrics {
  if (!(servingMl > 0) || abvPercent == null || !(abvPercent > 0)) {
    return {
      pureAlcoholMl: null,
      pureAlcoholFlOz: null,
      standardDrinks: null,
      alcoholPer100Calories: null,
      alcoholPer100CaloriesFlOz: null,
      standardDrinksPer100Calories: null,
      caloriesPerStandardDrink: null,
    };
  }
  const pureMl = pureAlcoholMl(servingMl, abvPercent);
  const alcPer100 =
    calories != null
      ? alcoholPer100Calories(servingMl, abvPercent, calories)
      : null;
  return {
    pureAlcoholMl: pureMl,
    pureAlcoholFlOz: mlToFlOz(pureMl),
    standardDrinks: standardDrinks(servingMl, abvPercent),
    alcoholPer100Calories: alcPer100,
    alcoholPer100CaloriesFlOz:
      alcPer100 != null ? mlToFlOz(alcPer100) : null,
    standardDrinksPer100Calories:
      calories != null
        ? standardDrinksPer100Calories(servingMl, abvPercent, calories)
        : null,
    caloriesPerStandardDrink:
      calories != null
        ? caloriesPerStandardDrink(servingMl, abvPercent, calories)
        : null,
  };
}

export interface PackageMetrics {
  packageMl: number;
  packageAlcoholMl: number | null;
  packageStandardDrinks: number | null;
  alcoholPerDollar: number | null;
  alcoholPerDollarFlOz: number | null;
  standardDrinksPerDollar: number | null;
  dollarsPerStandardDrink: number | null;
  servingPriceUsd: number | null;
}

export function computePackageMetrics(
  containerCount: number,
  containerMl: number,
  abvPercent: number | null,
  packagePriceUsd: number | null,
  servingMl: number,
): PackageMetrics {
  const pkgMl = packageMl(containerCount, containerMl);
  if (
    !(containerCount > 0) ||
    !(containerMl > 0) ||
    abvPercent == null ||
    !(abvPercent > 0)
  ) {
    return {
      packageMl: pkgMl,
      packageAlcoholMl: null,
      packageStandardDrinks: null,
      alcoholPerDollar: null,
      alcoholPerDollarFlOz: null,
      standardDrinksPerDollar: null,
      dollarsPerStandardDrink: null,
      servingPriceUsd:
        packagePriceUsd != null
          ? servingPriceUsd(packagePriceUsd, servingMl, containerCount, containerMl)
          : null,
    };
  }
  const alcPerDollar =
    packagePriceUsd != null
      ? alcoholPerDollar(containerCount, containerMl, abvPercent, packagePriceUsd)
      : null;
  return {
    packageMl: pkgMl,
    packageAlcoholMl: packageAlcoholMl(containerCount, containerMl, abvPercent),
    packageStandardDrinks: packageStandardDrinks(
      containerCount,
      containerMl,
      abvPercent,
    ),
    alcoholPerDollar: alcPerDollar,
    alcoholPerDollarFlOz: alcPerDollar != null ? mlToFlOz(alcPerDollar) : null,
    standardDrinksPerDollar:
      packagePriceUsd != null
        ? standardDrinksPerDollar(
            containerCount,
            containerMl,
            abvPercent,
            packagePriceUsd,
          )
        : null,
    dollarsPerStandardDrink:
      packagePriceUsd != null
        ? dollarsPerStandardDrink(
            containerCount,
            containerMl,
            abvPercent,
            packagePriceUsd,
          )
        : null,
    servingPriceUsd:
      packagePriceUsd != null
        ? servingPriceUsd(packagePriceUsd, servingMl, containerCount, containerMl)
        : null,
  };
}
